import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const Stat = ({ label, value, testid, cls = "" }) => (
  <div className="p-4 rounded-md border border-border bg-card" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
    <p className={`font-display text-2xl font-bold mt-1 tabular-nums ${cls}`}>{money(value)}</p>
  </div>
);

export default function MonthlySummary() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/dashboard/monthly-summary?year=${year}`).then(r => setD(r.data)).catch(() => {}); }, [year]);

  const chart = (d?.months || []).map((m, i) => ({ name: MONTHS[i], Service: m.service_charge, Parts: m.parts_sale, Profit: m.parts_profit }));
  const t = d?.total || {};
  return (
    <Card className="p-6 border border-border" data-testid="monthly-summary">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display font-semibold">Month-wise Service vs Parts</h3>
          <p className="text-xs text-muted-foreground">Tax Invoice + Cash Memo dono · GST hata kar, discount ke baad · Parts cost = item master purchase price</p>
        </div>
        <select value={year} onChange={e => setYear(Number(e.target.value))} data-testid="monthly-summary-year"
          className="h-9 px-3 border border-border rounded-md bg-card text-sm">
          {[0, 1, 2, 3].map(x => <option key={x} value={thisYear - x}>{thisYear - x}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Stat label="Service Charge" value={t.service_charge || 0} testid="ms-total-service" />
        <Stat label="Parts Sale" value={t.parts_sale || 0} testid="ms-total-parts" />
        <Stat label="Parts Cost" value={t.parts_cost || 0} testid="ms-total-cost" />
        <Stat label="Parts Net Profit" value={t.parts_profit || 0} testid="ms-total-profit" cls={(t.parts_profit || 0) < 0 ? "text-red-600" : "text-emerald-700"} />
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={chart} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(214.3 31.8% 91.4%)" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip formatter={(v) => money(v)} />
          <Legend />
          <Bar dataKey="Service" fill="hsl(199 89% 48%)" radius={[3, 3, 0, 0]} />
          <Bar dataKey="Parts" fill="hsl(24.6 95% 53.1%)" radius={[3, 3, 0, 0]} />
          <Bar dataKey="Profit" fill="hsl(142.1 76.2% 36.3%)" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-4 border border-border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead><TableHead className="text-right">Bills</TableHead>
              <TableHead className="text-right">Service Charge</TableHead><TableHead className="text-right">Parts Sale</TableHead>
              <TableHead className="text-right">Parts Cost</TableHead><TableHead className="text-right">Parts Net Profit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(d?.months || []).map((m, i) => (
              <TableRow key={m.month} data-testid={`ms-row-${m.month}`} className={m.bills ? "" : "text-muted-foreground"}>
                <TableCell>{MONTHS[i]} {year}</TableCell>
                <TableCell className="text-right tabular-nums">{m.bills}</TableCell>
                <TableCell className="text-right tabular-nums">{money(m.service_charge)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(m.parts_sale)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(m.parts_cost)}</TableCell>
                <TableCell className={`text-right tabular-nums font-semibold ${m.parts_profit < 0 ? "text-red-600" : "text-emerald-700"}`}>{money(m.parts_profit)}</TableCell>
              </TableRow>
            ))}
            <TableRow className="font-bold bg-slate-50" data-testid="ms-row-total">
              <TableCell>Total</TableCell>
              <TableCell className="text-right tabular-nums">{t.bills || 0}</TableCell>
              <TableCell className="text-right tabular-nums">{money(t.service_charge || 0)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(t.parts_sale || 0)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(t.parts_cost || 0)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(t.parts_profit || 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
